import { isSaveOnlyTerminalStep } from './sop-terminal-delivery.domain.js';
import { customListValidationIssues, isCustomListStep } from './custom-list-step.domain.js';
import { isMultipleChoiceStep, multipleChoiceValidationIssues } from './multiple-choice-step.domain.js';
import {
  hasOperatorInstructions,
  MISSING_OPERATOR_INSTRUCTIONS_MESSAGE,
  stepRequiresOperatorInstructions,
} from './sop-operator-instructions.domain.js';
import {
  getSopStepOutgoingEdges,
  type SopStepOutgoingEdgeKind,
} from './sop-step-edges.domain.js';

export type SopFlowStep = {
  id?: unknown;
  draft_step_id?: string | null;
  step_order?: unknown;
  archetype?: string | null;
  variant?: string | null;
  title?: string | null;
  prompt_text?: string | null;
  url?: string | null;
  url_reference_value?: string | null;
  include_in_report?: boolean | number | null;
  next_step?: unknown;
  yes_next_step?: unknown;
  no_next_step?: unknown;
  choice_positive_label?: string | null;
  choice_negative_label?: string | null;
  config?: unknown;
  contact_reference_type?: string | null;
  contact_reference_value?: unknown;
};

export const BRANCH_DESTINATION_BLOCKING_CAUSES = new Set([
  'missing-yes-path',
  'missing-no-path',
  'missing-option-path',
  'invalid-destination',
]);

/** Canonical step_order, id sort — match frontend sortSopSteps and repo getSteps ORDER BY. */
export function sortSopFlowSteps<T extends SopFlowStep>(steps: T[]): T[] {
  return steps.slice().sort((a, b) => {
    const oa = Number(a.step_order) || 0;
    const ob = Number(b.step_order) || 0;
    if (oa !== ob) return oa - ob;
    return (Number(a.id) || 0) - (Number(b.id) || 0);
  });
}

/** Apply a proposed reorder (ordered step IDs) for validation before persistence. */
export function applyProposedStepOrder<T extends SopFlowStep>(
  steps: T[],
  orderedStepIds: number[],
): T[] {
  const byId = new Map<number, T>();
  for (const step of steps) {
    const id = Number(step.id);
    if (Number.isFinite(id) && id > 0) {
      byId.set(id, step);
    }
  }
  return orderedStepIds.map((stepId, index) => {
    const step = byId.get(stepId);
    if (!step) {
      throw new Error(`Unknown step id ${stepId} in proposed order`);
    }
    return { ...step, step_order: index + 1 };
  });
}

export type SopFlowIncompletePath = {
  path: string[];
  cause: string;
};

function normalizeStepId(value: unknown): string | null {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return String(numeric);
}

function findStepById(steps: SopFlowStep[], stepId: unknown) {
  const key = normalizeStepId(stepId);
  if (!key) return null;
  return steps.find((s) => normalizeStepId(s.id) === key) ?? null;
}

function hasIncomingLinks(steps: SopFlowStep[], stepId: unknown) {
  const key = normalizeStepId(stepId);
  if (!key) return false;
  return steps.some((source) =>
    getSopStepOutgoingEdges(source).some((edge) => normalizeStepId(edge.targetId) === key),
  );
}

function missingCauseForEdge(kind: SopStepOutgoingEdgeKind): string {
  switch (kind) {
    case 'yes':
      return 'missing-yes-path';
    case 'no':
      return 'missing-no-path';
    case 'option':
      return 'missing-option-path';
    default:
      return 'missing-next-step';
  }
}

function stepDisplayLabel(steps: SopFlowStep[], stepId: string): string {
  const step = findStepById(steps, stepId);
  if (!step) return `Step #${stepId}`;
  const order = Number(step.step_order);
  const orderLabel = Number.isFinite(order) && order > 0 ? `Step ${order}` : `Step #${stepId}`;
  const title = String(step.title || '').trim();
  if (String(step.archetype || '').toUpperCase() === 'TERMINAL') {
    return isSaveOnlyTerminalStep(step) ? `${orderLabel} "Save Report Only"` : `${orderLabel} "End SOP"`;
  }
  return title ? `${orderLabel} "${title}"` : orderLabel;
}

export function formatSopFlowPathLabel(steps: SopFlowStep[], pathIds: string[]): string {
  if (!pathIds.length) return '';
  const labels = pathIds.map((id) => stepDisplayLabel(steps, id));
  return labels.join(' → ');
}

const CAUSE_LABELS: Record<string, string> = {
  cycle: 'cycle',
  'missing-next-step': 'missing next step',
  'missing-yes-path': 'This branch needs a destination',
  'missing-no-path': 'This branch needs a destination',
  'missing-option-path': 'This branch needs a destination',
  'invalid-destination': 'This branch points to a step that no longer exists',
  'step-not-found': 'This branch points to a step that no longer exists',
};

export function sopFlowIncompletePathKey(entry: SopFlowIncompletePath): string {
  return `${entry.cause}:${entry.path.join('>')}`;
}

function pathSignature(entry: SopFlowIncompletePath): string {
  return sopFlowIncompletePathKey(entry);
}

function formatIncompletePathEntry(steps: SopFlowStep[], entry: SopFlowIncompletePath): string {
  const pathLabel = formatSopFlowPathLabel(steps, entry.path);
  if (entry.cause === 'cycle' && entry.path.length >= 2) {
    return `Cycle: ${pathLabel}`;
  }
  const causeLabel = CAUSE_LABELS[entry.cause] ?? entry.cause;
  return pathLabel ? `${causeLabel} at ${pathLabel}` : causeLabel;
}

function formatIncompleteFlowDetails(
  steps: SopFlowStep[],
  pathsNotEnding: SopFlowIncompletePath[],
): { summary: string; details: string; paths: SopFlowIncompletePath[] } {
  if (!pathsNotEnding.length) {
    return { summary: 'SOP has incomplete flow paths', details: 'SOP has incomplete flow paths', paths: [] };
  }

  const unique: SopFlowIncompletePath[] = [];
  const seen = new Set<string>();
  for (const entry of pathsNotEnding) {
    const sig = pathSignature(entry);
    if (seen.has(sig)) continue;
    seen.add(sig);
    unique.push(entry);
  }

  const formatted = unique.slice(0, 3).map((entry) => formatIncompletePathEntry(steps, entry));
  const hasCycle = unique.some((entry) => entry.cause === 'cycle');
  const cyclePathLabels = formatted
    .filter((line) => line.startsWith('Cycle:'))
    .map((line) => line.slice('Cycle: '.length));
  const nonCycleLines = formatted.filter((line) => !line.startsWith('Cycle:'));
  let summary: string;
  if (hasCycle && nonCycleLines.length === 0) {
    summary = `SOP has a flow cycle (${cyclePathLabels.join('; ')})`;
  } else if (hasCycle) {
    summary = `SOP has a flow cycle (${cyclePathLabels.join('; ')}) and incomplete paths (${nonCycleLines.join('; ')})`;
  } else {
    summary = `SOP has incomplete flow paths (${nonCycleLines.join('; ')})`;
  }
  const details = formatted.join('\n');
  return { summary, details, paths: unique };
}

export type SopFlowStructureIssue =
  | 'no_steps'
  | 'no_end_step'
  | 'no_submit_terminal'
  | 'no_reachable_submit_terminal'
  | 'incomplete_flow'
  | 'missing_contact_reference';

export type SopFlowStructureResult =
  | { valid: true }
  | {
      valid: false;
      issue: SopFlowStructureIssue;
      reason: string;
      details?: string;
      incompletePaths?: SopFlowIncompletePath[];
    };

/** Pure SOP flow graph validation — no I/O or admin notification side effects. */
export function evaluateSopFlowStructure(steps: SopFlowStep[] | null | undefined): SopFlowStructureResult {
  if (!steps || !Array.isArray(steps) || steps.length === 0) {
    return { valid: false, issue: 'no_steps', reason: 'SOP has no steps', details: 'SOP has no steps' };
  }

  const flowSteps = sortSopFlowSteps(steps);

  const endStep = flowSteps.find((s) => s.archetype === 'TERMINAL');
  if (!endStep) {
    return {
      valid: false,
      issue: 'no_end_step',
      reason: 'SOP does not have an End SOP step',
      details: 'SOP does not have an End SOP step',
    };
  }

  const terminalSteps = flowSteps.filter((s) => s.archetype === 'TERMINAL');
  const submitReachable = new Set<string>();
  function collectReachableTerminals(stepId: string, seen: Set<string>) {
    if (!stepId || seen.has(stepId)) return;
    seen.add(stepId);
    const step = flowSteps.find((s) => String(s.id) === String(stepId));
    if (!step) return;
    if (step.archetype === 'TERMINAL') {
      if (!isSaveOnlyTerminalStep(step)) submitReachable.add(String(step.id));
      return;
    }
    for (const edge of getSopStepOutgoingEdges(step)) {
      if (edge.targetId != null) {
        collectReachableTerminals(String(edge.targetId), new Set(seen));
      }
    }
  }
  const entryPointsForSubmit = flowSteps.filter((step) => {
    if (step.step_order === 1) return true;
    return !hasIncomingLinks(flowSteps, step.id);
  });
  entryPointsForSubmit.forEach((step) => {
    if (step.archetype !== 'TERMINAL') collectReachableTerminals(String(step.id), new Set());
  });
  const hasSubmitTerminalStep = terminalSteps.some((s) => !isSaveOnlyTerminalStep(s));
  if (terminalSteps.length > 0 && !hasSubmitTerminalStep) {
    return {
      valid: false,
      issue: 'no_submit_terminal',
      reason: 'No submit terminal step',
      details: 'Add at least one normal End SOP (submit) terminal step.',
    };
  }
  if (hasSubmitTerminalStep && submitReachable.size === 0) {
    return {
      valid: false,
      issue: 'no_reachable_submit_terminal',
      reason: 'No reachable submit terminal',
      details: 'Add or link to a normal End SOP terminal so at least one execution path can trigger delivery.',
    };
  }

  const pathsNotEnding: SopFlowIncompletePath[] = [];
  function recordIncomplete(path: string[], cause: string) {
    pathsNotEnding.push({ path: [...path], cause: cause || 'unknown' });
  }
  function findIncompletePaths(stepId: unknown, path: string[] = []) {
    const normalizedStepId = normalizeStepId(stepId);
    if (!normalizedStepId) {
      recordIncomplete(path, 'missing-next-step');
      return;
    }
    if (path.includes(normalizedStepId)) {
      recordIncomplete([...path, normalizedStepId], 'cycle');
      return;
    }
    const step = findStepById(flowSteps, normalizedStepId);
    if (!step) {
      recordIncomplete(path, 'invalid-destination');
      return;
    }
    if (step.archetype === 'TERMINAL') return;
    const newPath = [...path, normalizedStepId];
    for (const edge of getSopStepOutgoingEdges(step)) {
      if (edge.targetId == null) {
        recordIncomplete(newPath, missingCauseForEdge(edge.kind));
        continue;
      }
      const targetKey = normalizeStepId(edge.targetId);
      if (!targetKey) {
        recordIncomplete(newPath, missingCauseForEdge(edge.kind));
        continue;
      }
      if (newPath.includes(targetKey)) {
        recordIncomplete([...newPath, targetKey], 'cycle');
        continue;
      }
      const targetStep = findStepById(flowSteps, edge.targetId);
      if (!targetStep) {
        recordIncomplete(newPath, 'invalid-destination');
        continue;
      }
      if (targetStep.archetype === 'TERMINAL') continue;
      findIncompletePaths(edge.targetId, newPath);
    }
  }

  const entryPoints = flowSteps.filter((step) => {
    if (Number(step.step_order) === 1) return true;
    return !hasIncomingLinks(flowSteps, step.id);
  });
  entryPoints.forEach((step) => {
    if (step.archetype !== 'TERMINAL') findIncompletePaths(step.id);
  });

  if (pathsNotEnding.length > 0) {
    const flowDetails = formatIncompleteFlowDetails(flowSteps, pathsNotEnding);
    return {
      valid: false,
      issue: 'incomplete_flow',
      reason: flowDetails.summary,
      details: flowDetails.details,
      incompletePaths: flowDetails.paths,
    };
  }

  const stepsWithBrokenReferences = flowSteps.filter(
    (step) =>
      (step.contact_reference_type === 'EXPLICIT_GROUP' || step.contact_reference_type === 'EXPLICIT_LIST') &&
      (step.contact_reference_value === null || step.contact_reference_value === undefined),
  );
  if (stepsWithBrokenReferences.length > 0) {
    const step = stepsWithBrokenReferences[0];
    const referenceType = step.contact_reference_type === 'EXPLICIT_GROUP' ? 'group' : 'list';
    return {
      valid: false,
      issue: 'missing_contact_reference',
      reason: `Step is configured to use a specific ${referenceType}, but no ${referenceType} is selected`,
      details: `Step is configured with contact_reference_type = '${step.contact_reference_type}' but contact_reference_value is NULL.`,
    };
  }

  return { valid: true };
}

export function formatSopOwnerLabel(ownerType?: string | null, ownerName?: string | null): string {
  const type = String(ownerType || '').toUpperCase();
  if (type === 'GLOBAL') return 'Global';
  if (type === 'DEALER') return ownerName ? `Dealer: ${ownerName}` : 'Dealer';
  if (type === 'SITE') return ownerName ? `Site: ${ownerName}` : 'Site';
  if (ownerName) return ownerName;
  return '';
}

export function extractMinimalCyclePath(path: string[]): string[] {
  if (path.length < 2) return [...path];
  const repeated = path[path.length - 1];
  const idx = path.indexOf(repeated);
  if (idx >= 0) return path.slice(idx);
  return [...path];
}

export function minimalCycleSignature(path: string[]): string {
  return extractMinimalCyclePath(path).join('>');
}

function resolveSopFlowStepDisplayNumber(steps: SopFlowStep[], stepId: string): number {
  const sorted = sortSopFlowSteps(steps);
  const key = normalizeStepId(stepId);
  if (!key) return 0;
  const idx = sorted.findIndex((step) => normalizeStepId(step.id) === key);
  if (idx >= 0) return idx + 1;
  const step = findStepById(steps, stepId);
  const order = Number(step?.step_order);
  return Number.isFinite(order) && order > 0 ? order : 0;
}

export function formatSopFlowCycleStepNumberChain(
  steps: SopFlowStep[],
  pathIds: string[],
  displayNumberForId?: (stepId: string) => number | undefined,
): number[] {
  return extractMinimalCyclePath(pathIds)
    .map((id) => {
      const mapped = displayNumberForId?.(id);
      if (mapped != null && mapped > 0) return mapped;
      return resolveSopFlowStepDisplayNumber(steps, id);
    })
    .filter((value) => value > 0);
}

export function formatSopFlowCycleStepNumberChainLabel(stepNumbers: number[]): string {
  return stepNumbers.map((value) => `Step ${value}`).join(' → ');
}

function sopFlowBackEdgeKey(steps: SopFlowStep[], entry: SopFlowIncompletePath): string {
  const backEdge = inferSopFlowBackEdge(steps, entry.path);
  if (!backEdge) return minimalCycleSignature(entry.path);
  const targetId = entry.path[entry.path.length - 1] ?? '';
  return `${backEdge.stepId}:${backEdge.branch}:${targetId}`;
}

export function dedupeCyclePaths(
  paths: SopFlowIncompletePath[],
  steps?: SopFlowStep[],
): SopFlowIncompletePath[] {
  const cycles = paths.filter((entry) => entry.cause === 'cycle');
  const others = paths.filter((entry) => entry.cause !== 'cycle');
  const seenMinimal = new Set<string>();
  const afterMinimal: SopFlowIncompletePath[] = [];
  for (const entry of cycles) {
    const sig = minimalCycleSignature(entry.path);
    if (seenMinimal.has(sig)) continue;
    seenMinimal.add(sig);
    afterMinimal.push(entry);
  }

  if (!steps?.length) {
    return [...afterMinimal, ...others];
  }

  const seenBackEdge = new Map<string, SopFlowIncompletePath>();
  for (const entry of afterMinimal) {
    const key = sopFlowBackEdgeKey(steps, entry);
    const existing = seenBackEdge.get(key);
    if (!existing) {
      seenBackEdge.set(key, entry);
      continue;
    }
    const existingLen = extractMinimalCyclePath(existing.path).length;
    const newLen = extractMinimalCyclePath(entry.path).length;
    if (newLen < existingLen) {
      seenBackEdge.set(key, entry);
    }
  }
  return [...seenBackEdge.values(), ...others];
}

export type SopFlowBackEdge = {
  stepId: string;
  branch: 'Next' | 'Yes' | 'No' | string;
};

export function inferSopFlowBackEdge(steps: SopFlowStep[], path: string[]): SopFlowBackEdge | null {
  if (path.length < 2) return null;
  const backEdgeId = path[path.length - 2];
  const targetId = path[path.length - 1];
  const step = findStepById(steps, backEdgeId);
  if (!step) return null;
  for (const edge of getSopStepOutgoingEdges(step)) {
    if (normalizeStepId(edge.targetId) === normalizeStepId(targetId)) {
      if (edge.kind === 'yes') return { stepId: backEdgeId, branch: 'Yes' };
      if (edge.kind === 'no') return { stepId: backEdgeId, branch: 'No' };
      if (edge.kind === 'option') return { stepId: backEdgeId, branch: edge.label || 'Option' };
      return { stepId: backEdgeId, branch: 'Next' };
    }
  }
  return { stepId: backEdgeId, branch: 'Next' };
}

function structureHasCycle(result: SopFlowStructureResult): boolean {
  if (result.valid !== false) return false;
  if (result.issue !== 'incomplete_flow') return false;
  return (result.incompletePaths ?? []).some((entry) => entry.cause === 'cycle');
}

function shortStructureIssueLabel(result: Extract<SopFlowStructureResult, { valid: false }>): string {
  switch (result.issue) {
    case 'no_steps':
      return 'has no steps';
    case 'no_end_step':
      return 'does not have an End SOP step';
    case 'no_submit_terminal':
      return 'has only save-only terminals and cannot submit for delivery';
    case 'no_reachable_submit_terminal':
      return 'has no reachable submit (delivery) terminal';
    case 'incomplete_flow':
      return structureHasCycle(result) ? 'has a flow loop' : 'has incomplete flow paths';
    case 'missing_contact_reference':
      return result.reason;
    default:
      return result.reason;
  }
}

export function deriveSopFlowFocusStepId(
  steps: SopFlowStep[],
  result: Extract<SopFlowStructureResult, { valid: false }>,
): number | undefined {
  if (result.issue === 'missing_contact_reference') {
    const broken = steps.find(
      (step) =>
        (step.contact_reference_type === 'EXPLICIT_GROUP' || step.contact_reference_type === 'EXPLICIT_LIST') &&
        (step.contact_reference_value === null || step.contact_reference_value === undefined),
    );
    const id = Number(broken?.id);
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }
  const paths = result.incompletePaths ?? [];
  const cyclePath = paths.find((entry) => entry.cause === 'cycle');
  if (cyclePath && cyclePath.path.length >= 2) {
    const backEdge = inferSopFlowBackEdge(steps, cyclePath.path);
    if (backEdge) {
      const id = Number(backEdge.stepId);
      if (Number.isFinite(id) && id > 0) return id;
    }
    const fallback = Number(cyclePath.path[0]);
    if (Number.isFinite(fallback) && fallback > 0) return fallback;
  }
  const incomplete = paths.find((entry) => entry.cause !== 'cycle' && entry.path.length > 0);
  if (incomplete) {
    const id = Number(incomplete.path[incomplete.path.length - 1]);
    if (Number.isFinite(id) && id > 0) return id;
  }
  return undefined;
}

export type SopFlowAdminNotificationContext = {
  sopName: string;
  ownerType?: string | null;
  ownerName?: string | null;
  scenarioContext?: { siteName: string; scenarioName: string };
};

export type SopFlowAdminNotificationCopy = {
  shortLabel: string;
  message: string;
  details?: string;
  fixPage: 'sops' | 'sites-admin';
  focusStepId?: number;
};

export function formatSopFlowAdminNotificationCopy(
  result: Extract<SopFlowStructureResult, { valid: false }>,
  context: SopFlowAdminNotificationContext,
): SopFlowAdminNotificationCopy {
  const ownerLabel = formatSopOwnerLabel(context.ownerType, context.ownerName);
  const ownerSuffix = ownerLabel ? ` (${ownerLabel})` : '';
  const issueLabel = shortStructureIssueLabel(result);
  const shortLabel = issueLabel.startsWith('has ') || issueLabel.startsWith('does ')
    ? issueLabel
    : issueLabel;
  const sopPhrase = `SOP "${context.sopName}"${ownerSuffix}`;
  const fixHint = 'Fix the workflow in SOPs Admin.';
  let message: string;
  if (context.scenarioContext) {
    message = `Scenario "${context.scenarioContext.scenarioName}" on site "${context.scenarioContext.siteName}" uses ${sopPhrase}, which ${shortLabel}. ${fixHint}`;
  } else {
    message = `${sopPhrase} ${shortLabel}. ${fixHint}`;
  }
  return {
    shortLabel,
    message,
    fixPage: 'sops',
  };
}

export function formatSopFlowAdminNotificationCopyWithSteps(
  steps: SopFlowStep[],
  result: Extract<SopFlowStructureResult, { valid: false }>,
  context: SopFlowAdminNotificationContext,
): SopFlowAdminNotificationCopy {
  const copy = formatSopFlowAdminNotificationCopy(result, context);
  copy.focusStepId = deriveSopFlowFocusStepId(steps, result);
  return copy;
}

export type SopFlowSaveRejection = {
  title: string;
  loopPath: string;
  fixAt: string;
  fixHint: string;
  focusStepId?: number;
  message: string;
};

export function stepSaveTouchesLinks(updates: Record<string, unknown>): boolean {
  return (
    'next_step' in updates ||
    'yes_next_step' in updates ||
    'no_next_step' in updates ||
    'config' in updates
  );
}

/** Whether flow-structure validation should hard-block a step save. */
export function sopFlowStructureBlocksSave(result: SopFlowStructureResult): boolean {
  if (result.valid !== false) return false;
  if (result.issue === 'no_reachable_submit_terminal' || result.issue === 'no_submit_terminal') {
    return true;
  }
  if (result.issue !== 'incomplete_flow') return false;
  const paths = result.incompletePaths ?? [];
  if (paths.some((entry) => entry.cause === 'cycle')) return true;
  return paths.some((entry) => BRANCH_DESTINATION_BLOCKING_CAUSES.has(entry.cause));
}

function countUnlinkedOutgoingEdges(step: SopFlowStep): number {
  return getSopStepOutgoingEdges(step).filter((edge) => edge.targetId == null).length;
}

function incompletePathFocusStepId(entry: SopFlowIncompletePath): string | null {
  if (!entry.path.length) return null;
  return normalizeStepId(entry.path[entry.path.length - 1]);
}

function missingBranchLabelFromCause(cause: string, step: SopFlowStep | null): string {
  if (cause === 'missing-yes-path') {
    return String(step?.choice_positive_label || 'Yes').trim() || 'Yes';
  }
  if (cause === 'missing-no-path') {
    return String(step?.choice_negative_label || 'No').trim() || 'No';
  }
  if (cause === 'missing-option-path') {
    const firstMissing = getSopStepOutgoingEdges(step ?? {}).find((edge) => edge.targetId == null);
    return firstMissing?.label?.trim() || 'Choice';
  }
  return 'outgoing';
}

export function sopFlowIncompleteCauseBlocksSave(cause: string): boolean {
  if (cause === 'missing-next-step') return false;
  if (cause === 'cycle') return true;
  return BRANCH_DESTINATION_BLOCKING_CAUSES.has(cause);
}

/**
 * Scoped link-save gate: block cycles globally and branch/destination defects on the
 * step being edited when the save does not reduce unlinked outgoing edges. Other steps'
 * missing branches and linear missing-next-step warnings do not block incremental repair.
 */
export function sopFlowStructureBlocksStepLinkSave(
  stepId: number,
  beforeSteps: SopFlowStep[],
  afterSteps: SopFlowStep[],
): boolean {
  const afterStructure = evaluateSopFlowStructure(afterSteps);
  const beforeStep = findStepById(beforeSteps, stepId);
  const afterStep = findStepById(afterSteps, stepId);
  const beforeUnlinked = beforeStep ? countUnlinkedOutgoingEdges(beforeStep) : 0;
  const afterUnlinked = afterStep ? countUnlinkedOutgoingEdges(afterStep) : 0;
  const unlinkedImproved = afterUnlinked < beforeUnlinked;

  if (afterUnlinked > beforeUnlinked) {
    return true;
  }

  if (afterStructure.valid !== false) return false;

  const paths = afterStructure.incompletePaths ?? [];
  const savedKey = normalizeStepId(stepId);

  if (afterStructure.issue === 'incomplete_flow' && paths.some((entry) => entry.cause === 'cycle')) {
    return true;
  }

  if (
    afterStructure.issue === 'no_submit_terminal' ||
    afterStructure.issue === 'no_reachable_submit_terminal' ||
    afterStructure.issue === 'no_end_step' ||
    afterStructure.issue === 'no_steps' ||
    afterStructure.issue === 'missing_contact_reference'
  ) {
    return false;
  }

  if (afterStructure.issue !== 'incomplete_flow' || !savedKey) return false;

  for (const entry of paths) {
    if (incompletePathFocusStepId(entry) !== savedKey) continue;
    if (entry.cause === 'invalid-destination') return true;
    if (
      entry.cause === 'missing-yes-path' ||
      entry.cause === 'missing-no-path' ||
      entry.cause === 'missing-option-path'
    ) {
      if (!unlinkedImproved) return true;
    }
  }

  return false;
}

export type SopFlowCycleSummary = {
  summary: string;
  fixAt: string;
  focusStepId?: number;
  fromStepId: string;
  toStepId: string;
  branch: SopFlowBackEdge['branch'] | null;
  /** Minimal cycle step IDs — for UI loop chain segments. */
  loopStepIds: string[];
  /** Admin display numbers for the minimal cycle. */
  loopStepNumbers: number[];
  /** Full loop path — diagnostics and expanded detail only. */
  pathLabel: string;
};

function sopFlowCycleBranchSuffix(branch: SopFlowBackEdge['branch'] | null): string {
  return branch && branch !== 'Next' ? ` (${branch})` : '';
}

/** Concise loop copy naming the back-edge step, re-entry target, and step-number cycle. */
export function formatSopFlowCycleSummary(
  steps: SopFlowStep[],
  cyclePath: SopFlowIncompletePath,
  displayNumberForId?: (stepId: string) => number | undefined,
): SopFlowCycleSummary {
  const pathLabel = formatSopFlowPathLabel(steps, cyclePath.path);
  const backEdge = inferSopFlowBackEdge(steps, cyclePath.path);
  const fromStepId = backEdge?.stepId ?? cyclePath.path[cyclePath.path.length - 2] ?? '';
  const toStepId = cyclePath.path[cyclePath.path.length - 1] ?? '';
  const fromLabel = stepDisplayLabel(steps, fromStepId);
  const toLabel = stepDisplayLabel(steps, toStepId);
  const branch = backEdge?.branch ?? null;
  const branchSuffix = sopFlowCycleBranchSuffix(branch);
  const loopStepIds = extractMinimalCyclePath(cyclePath.path);
  const loopStepNumbers = formatSopFlowCycleStepNumberChain(steps, cyclePath.path, displayNumberForId);
  const loopChain = formatSopFlowCycleStepNumberChainLabel(loopStepNumbers);
  const summary = loopChain
    ? `${fromLabel}${branchSuffix} links to ${toLabel}. Loop: ${loopChain}.`
    : `${fromLabel}${branchSuffix} links to ${toLabel}.`;
  const fixAt = backEdge
    ? `Change the link on ${fromLabel}${branchSuffix}.`
    : 'Change the outgoing link that closes this loop.';
  const focusId = backEdge ? Number(backEdge.stepId) : Number(cyclePath.path[cyclePath.path.length - 2]);
  return {
    summary,
    fixAt,
    focusStepId: Number.isFinite(focusId) && focusId > 0 ? focusId : undefined,
    fromStepId,
    toStepId,
    branch,
    loopStepIds,
    loopStepNumbers,
    pathLabel,
  };
}

export function formatSopFlowCycleGuidance(
  steps: SopFlowStep[],
  cyclePath: SopFlowIncompletePath,
): { pathLabel: string; fixAt: string; focusStepId?: number } {
  const { pathLabel, fixAt, focusStepId } = formatSopFlowCycleSummary(steps, cyclePath);
  return { pathLabel, fixAt, focusStepId };
}

const SAVE_REJECTION_FIX_HINT =
  'That path must lead to an End SOP terminal (or a sequence of steps that ends at one). It cannot point back into a step already on this loop.';

export function formatSopFlowSaveRejection(
  steps: SopFlowStep[],
  result: Extract<SopFlowStructureResult, { valid: false }>,
): SopFlowSaveRejection {
  if (result.issue === 'incomplete_flow' && structureHasCycle(result)) {
    const cyclePath = dedupeCyclePaths(result.incompletePaths ?? [], steps).find((entry) => entry.cause === 'cycle');
    if (cyclePath) {
      const { summary, fixAt, focusStepId } = formatSopFlowCycleSummary(steps, cyclePath);
      return {
        title: 'Cannot save — flow loop',
        loopPath: summary,
        fixAt,
        fixHint: SAVE_REJECTION_FIX_HINT,
        focusStepId,
        message: `Cannot save — flow loop. ${summary} ${fixAt} ${SAVE_REJECTION_FIX_HINT}`,
      };
    }
  }
  if (result.issue === 'no_reachable_submit_terminal' || result.issue === 'no_submit_terminal') {
    const fixAt = 'Ensure at least one branch from the entry step reaches a submit End SOP terminal.';
    return {
      title: 'Cannot save — no path to End SOP',
      loopPath: '',
      fixAt,
      fixHint: SAVE_REJECTION_FIX_HINT,
      message: `Cannot save — no path to End SOP. ${fixAt} ${SAVE_REJECTION_FIX_HINT}`,
    };
  }
  const fixAt = 'Ensure every execution path reaches an End SOP terminal.';
  return {
    title: 'Cannot save — invalid flow',
    loopPath: result.details ?? result.reason,
    fixAt,
    fixHint: SAVE_REJECTION_FIX_HINT,
    message: `Cannot save — invalid flow. ${fixAt} ${SAVE_REJECTION_FIX_HINT}`,
  };
}

/** Rejection copy for scoped step link saves — names the blocking step and branch when possible. */
export function formatSopFlowStepLinkSaveRejection(
  savedStepId: number,
  beforeSteps: SopFlowStep[],
  afterSteps: SopFlowStep[],
): SopFlowSaveRejection {
  const afterStructure = evaluateSopFlowStructure(afterSteps);
  if (afterStructure.valid !== false) {
    return {
      title: 'Cannot save — invalid flow',
      loopPath: '',
      fixAt: 'Review the step links and try again.',
      fixHint: SAVE_REJECTION_FIX_HINT,
      message: 'Cannot save — invalid flow. Review the step links and try again.',
    };
  }

  if (afterStructure.issue === 'incomplete_flow' && structureHasCycle(afterStructure)) {
    return formatSopFlowSaveRejection(afterSteps, afterStructure);
  }

  const paths = afterStructure.incompletePaths ?? [];
  const savedKey = normalizeStepId(savedStepId);
  const afterStep = findStepById(afterSteps, savedStepId);
  const beforeStep = findStepById(beforeSteps, savedStepId);
  const beforeUnlinked = beforeStep ? countUnlinkedOutgoingEdges(beforeStep) : 0;
  const afterUnlinked = afterStep ? countUnlinkedOutgoingEdges(afterStep) : 0;
  const unlinkedImproved = afterUnlinked < beforeUnlinked;
  const stepLabel = savedKey ? stepDisplayLabel(afterSteps, savedKey) : `Step #${savedStepId}`;

  if (afterUnlinked > beforeUnlinked) {
    const branchLabel = missingBranchLabelFromCause('missing-yes-path', afterStep);
    const fixAt = `Restore or set a destination on ${stepLabel}.`;
    return {
      title: 'Cannot save — branch needs destination',
      loopPath: `${stepLabel} cannot have more unlinked branches than before.`,
      fixAt,
      fixHint: SAVE_REJECTION_FIX_HINT,
      focusStepId: savedStepId,
      message: `Cannot save — ${stepLabel} still needs a ${branchLabel} destination.`,
    };
  }

  const invalidOnSaved = paths.find(
    (entry) =>
      entry.cause === 'invalid-destination' && incompletePathFocusStepId(entry) === savedKey,
  );
  if (invalidOnSaved) {
    const fixAt = `${stepLabel} points to a step that no longer exists. Choose a valid destination.`;
    return {
      title: 'Cannot save — invalid destination',
      loopPath: fixAt,
      fixAt,
      fixHint: SAVE_REJECTION_FIX_HINT,
      focusStepId: savedStepId,
      message: `Cannot save — ${stepLabel} points to a step that no longer exists.`,
    };
  }

  if (!unlinkedImproved) {
    const missingBranch = paths.find(
      (entry) =>
        (entry.cause === 'missing-yes-path' ||
          entry.cause === 'missing-no-path' ||
          entry.cause === 'missing-option-path') &&
        incompletePathFocusStepId(entry) === savedKey,
    );
    if (missingBranch) {
      const branchLabel = missingBranchLabelFromCause(missingBranch.cause, afterStep);
      const fixAt = `Set a ${branchLabel} destination on ${stepLabel}.`;
      return {
        title: 'Cannot save — branch needs destination',
        loopPath: `${stepLabel} still needs a ${branchLabel} destination.`,
        fixAt,
        fixHint: SAVE_REJECTION_FIX_HINT,
        focusStepId: savedStepId,
        message: `Cannot save — ${stepLabel} still needs a ${branchLabel} destination.`,
      };
    }
  }

  return formatSopFlowSaveRejection(afterSteps, afterStructure);
}

export type SopActivationIssue = {
  kind: string;
  message: string;
  stepId?: number | string | null;
  draftStepId?: string | null;
  field?: 'prompt_text' | 'config' | 'url_reference_value' | 'contact_reference_type' | 'contact_reference_value';
};

function authoringStepLabel(step: SopFlowStep): string {
  const order = Number(step.step_order);
  const prefix = Number.isFinite(order) && order > 0 ? `Step ${order}` : 'This step';
  const title = String(step.title || '').trim();
  return title ? `${prefix} "${title}"` : prefix;
}

function authoringIssueForStep(
  step: SopFlowStep,
  issue: Omit<SopActivationIssue, 'stepId' | 'draftStepId'>,
): SopActivationIssue {
  const rawId = step.id;
  return {
    ...issue,
    stepId:
      typeof rawId === 'number' || typeof rawId === 'string'
        ? rawId
        : rawId == null
          ? null
          : String(rawId),
    draftStepId: step.draft_step_id ?? null,
  };
}

function isValidOperatorUrl(value: unknown): boolean {
  const text = String(value ?? '').trim();
  if (!text) return false;
  try {
    const parsed = new URL(text);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Step-content issues that must be repaired before the draft can be published. */
export function listSopStepAuthoringIssues(
  steps: SopFlowStep[] | null | undefined,
): SopActivationIssue[] {
  if (!steps?.length) return [];
  const issues: SopActivationIssue[] = [];

  for (const step of sortSopFlowSteps(steps)) {
    const archetype = String(step.archetype || '').toUpperCase();
    const label = authoringStepLabel(step);

    if (stepRequiresOperatorInstructions(archetype) && !hasOperatorInstructions(step.prompt_text)) {
      issues.push(authoringIssueForStep(step, {
        kind: 'missing-operator-instructions',
        field: 'prompt_text',
        message: `${label}: ${MISSING_OPERATOR_INSTRUCTIONS_MESSAGE}`,
      }));
    }

    if (isMultipleChoiceStep(archetype)) {
      for (const issue of multipleChoiceValidationIssues(step.config, {
        includeInReport: step.include_in_report !== false && step.include_in_report !== 0,
      })) {
        if (issue.severity !== 'block') continue;
        issues.push(authoringIssueForStep(step, {
          kind: `multiple-choice-${issue.code}`,
          field: 'config',
          message: `${label}: ${issue.message}`,
        }));
      }
    }

    if (isCustomListStep(archetype, step.variant)) {
      for (const issue of customListValidationIssues(step.config, {
        includeInReport: step.include_in_report !== false && step.include_in_report !== 0,
      })) {
        if (issue.severity !== 'block') continue;
        issues.push(authoringIssueForStep(step, {
          kind: `custom-list-${issue.code}`,
          field: 'config',
          message: `${label}: ${issue.message}`,
        }));
      }
    }

    if (archetype === 'RESOURCE_VIEW') {
      const url = step.url_reference_value ?? step.url;
      issues.push(...(!String(url ?? '').trim()
        ? [authoringIssueForStep(step, {
            kind: 'missing-resource-url',
            field: 'url_reference_value',
            message: `${label}: Enter the URL operators should open.`,
          })]
        : !isValidOperatorUrl(url)
          ? [authoringIssueForStep(step, {
              kind: 'invalid-resource-url',
              field: 'url_reference_value',
              message: `${label}: Enter a complete URL beginning with http:// or https://.`,
            })]
          : []));
    }

    if (archetype === 'CONTACT_ACTION') {
      const referenceType = String(step.contact_reference_type || '').trim();
      if (!referenceType) {
        issues.push(authoringIssueForStep(step, {
          kind: 'missing-contact-reference-type',
          field: 'contact_reference_type',
          message: `${label}: Select which contact, list, or runtime-site contact this step should use.`,
        }));
      } else if (
        referenceType.startsWith('EXPLICIT_')
        && (step.contact_reference_value == null || String(step.contact_reference_value).trim() === '')
      ) {
        const noun = referenceType === 'EXPLICIT_GROUP'
          ? 'contact group'
          : referenceType === 'EXPLICIT_CONTACT'
            ? 'contact'
            : 'contact list';
        issues.push(authoringIssueForStep(step, {
          kind: 'missing-contact-reference',
          field: 'contact_reference_value',
          message: `${label}: Select a ${noun}.`,
        }));
      }
    }
  }

  return issues;
}

/** Only block a step save for invalid fields included in that save. */
export function sopStepAuthoringIssueTouchesUpdates(
  issue: SopActivationIssue,
  updates: Record<string, unknown>,
): boolean {
  if ('archetype' in updates) return true;
  if (!issue.field) return false;
  if (issue.field === 'config') {
    return 'config' in updates || 'variant' in updates || 'include_in_report' in updates;
  }
  if (issue.field === 'url_reference_value') {
    return 'url_reference_value' in updates || 'url' in updates;
  }
  if (issue.field === 'contact_reference_type' || issue.field === 'contact_reference_value') {
    return 'contact_reference_type' in updates || 'contact_reference_value' in updates;
  }
  return issue.field in updates;
}

/**
 * Non-first, non-terminal steps with no incoming edges (frontend "Unreachable step").
 * Shared so activate gating matches library badges.
 */
export function listUnreachableSopSteps(steps: SopFlowStep[] | null | undefined): SopFlowStep[] {
  if (!steps?.length) return [];
  const flowSteps = sortSopFlowSteps(steps);
  const firstId = flowSteps[0] ? Number(flowSteps[0].id) : NaN;
  return flowSteps.filter((step) => {
    if (Number(step.id) === firstId) return false;
    if (String(step.archetype || '').toUpperCase() === 'TERMINAL') return false;
    return !hasIncomingLinks(flowSteps, step.id);
  });
}

/**
 * Whether an SOP may be activated for operators / Action Center.
 * Blocks on any shared structural invalidity from {@link evaluateSopFlowStructure}
 * (including incomplete paths / missing-next-step that do not block save) and
 * unreachable orphans. Save-blocking is intentionally narrower; activate is not.
 */
export function evaluateSopActivationReadiness(
  steps: SopFlowStep[] | null | undefined,
): { ok: true } | { ok: false; issues: SopActivationIssue[] } {
  const issues: SopActivationIssue[] = [];
  const structure = evaluateSopFlowStructure(steps);
  if (structure.valid === false) {
    const focusStepId = deriveSopFlowFocusStepId(steps ?? [], structure);
    const focusStep = focusStepId == null ? null : findStepById(steps ?? [], focusStepId);
    issues.push({
      kind: structure.issue,
      message: structure.details || structure.reason || 'SOP flow is invalid',
      stepId: focusStepId,
      draftStepId: focusStep?.draft_step_id ?? null,
    });
  }
  for (const issue of listSopStepAuthoringIssues(steps)) {
    const duplicateStructureContact =
      structure.valid === false
      && structure.issue === 'missing_contact_reference'
      && Number(issue.stepId) === Number(deriveSopFlowFocusStepId(steps ?? [], structure))
      && issue.kind === 'missing-contact-reference';
    if (!duplicateStructureContact) issues.push(issue);
  }
  for (const step of listUnreachableSopSteps(steps)) {
    const title = String(step.title || `Step ${step.id}`).trim();
    const rawId = step.id;
    const stepId =
      typeof rawId === 'number' || typeof rawId === 'string' ? rawId : rawId == null ? null : String(rawId);
    issues.push({
      kind: 'missing-incoming',
      message: `${title} is not reached from another step.`,
      stepId,
    });
  }
  if (issues.length === 0) return { ok: true };
  return { ok: false, issues };
}

export function formatSopActivationRejection(issues: SopActivationIssue[]): string {
  const lines = issues.map((issue) => `• ${issue.message}`);
  return `Cannot publish this SOP until builder issues are fixed:\n${lines.join('\n')}`;
}
